// 接続矢印の経路の編集ハンドル。
//
// 編集できる接続矢印を 1 本だけ選んでいるとき、見えている各直線区間の中央に、画面上で一定の大きさの
// ハンドル（表示 8px・当たり判定 24px）を出す。横の区間は上下、縦の区間は左右へドラッグでき、隣の区間が
// 追従する。端点の直近の区間は、接続点を保つ折れ（dogleg）を補って動かす。直線のハンドルをドラッグすると、
// 角丸の折れ線へ切り替えて同じ操作で経路を作る。
//
// 編集中は op を送らず、確定前の経路を下書き（connector-route-draft-store）で本人の画面だけに描く。
// ポインタを離す・Enter で 1 つの op にまとめて送り（履歴も 1 件）、Esc・pointercancel・ウィンドウの
// フォーカス喪失・アンマウント・他の利用者による変更では下書きを捨てて保存しない。
// 矢印キーは 1 CSS px（Shift で 10px）をキャンバスの座標へ換算して動かす。

import { useCallback, useEffect, useRef, useState } from "react";
import {
  CONNECTOR_ROUTE_CONSTRAINTS_MAX,
  connectorRouteOffsetLimitsFor,
  isSameConnectorRoute,
  normalizeCanvasSizePreset,
  resolveCanvasRect,
  resolveConnectorAppearance,
  validateConnectorPatchResult,
  type CanvasObject,
  type ConnectorCanvasObject,
  type ConnectorChannelAxis,
  type ConnectorRoute,
  type ConnectorRouteConstraint,
} from "@refinement-canvas/domain";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useConnectorDragStore, type LiveFrame } from "./store/connector-drag-store";
import { useConnectorRouteDraftStore } from "./store/connector-route-draft-store";
import { useCanvasOpDispatcherContext } from "./dispatcher-context";
import { useShowToast } from "./notice/use-show-toast";
import { useConnectorEditingAvailable } from "./connector-editing-capability";
import { screenToStage } from "../../lib/canvas-coordinates";
import { generateCanvasOpId } from "../../lib/canvas-op-id";
import {
  computeConnectorGeometry,
  connectorGeometryInputFor,
  moveConnectorSegment,
  type ConnectorFrame,
  type ConnectorGeometry,
  type ConnectorGeometryInput,
  type ConnectorPoint,
} from "../../lib/connector-geometry";

/** ハンドルの当たり判定の大きさ（画面上の px）。 */
const HANDLE_HIT_PX = 24;
/** ハンドルの表示の大きさ（画面上の px）。 */
const HANDLE_VISUAL_PX = 8;
/**
 * 編集中に接続矢印・接続先の図形が変わって編集を取り消したときの通知（他の利用者の変更のほか、
 * 自分の取り消し操作や拒否された op の巻き戻しでも起きるため、誰の変更かは書かない）。
 */
const REMOTE_CHANGE_MESSAGE =
  "接続矢印または接続先の図形が変更されたため、経路の編集を取り消しました。";
/** 手動経路のチャネルが上限に達して、端の区間を動かせないときの通知。 */
const ROUTE_LIMIT_MESSAGE = `経路の折れ曲がりが上限（${CONNECTOR_ROUTE_CONSTRAINTS_MAX} 本の区間）に達しているため、この区間は動かせません。中央の区間を動かすか、「自動経路に戻す」で経路を戻してください。`;

/** 編集の開始時点の状態（他の利用者による変更の検知に使う）。 */
interface EditSnapshot {
  readonly fromFrame: ConnectorFrame;
  readonly toFrame: ConnectorFrame;
  readonly connector: ConnectorCanvasObject;
}

/** 進行中の経路の編集。 */
interface RouteEditSession {
  readonly connectorId: string;
  readonly kind: "pointer" | "keyboard";
  readonly pointerId: number | null;
  /** 動かす経路の入力（直線から始めた場合は角丸の折れ線の自動経路）。 */
  readonly input: ConnectorGeometryInput;
  readonly geometry: ConnectorGeometry;
  readonly segmentIndex: number;
  /**
   * 編集を始めたハンドルの添字（表示中の区間の添字）。編集中もこの添字のハンドルの要素を使い続け、
   * フォーカスとポインタの捕捉を失わないようにする（直線から始めた編集では segmentIndex と異なる）。
   */
  readonly originHandleIndex: number;
  readonly axis: ConnectorChannelAxis;
  /** 直線から始めた編集か（確定時に線の形も角丸の折れ線へ切り替える）。 */
  readonly fromStraight: boolean;
  readonly snapshot: EditSnapshot;
  /** 区間の元の位置（ステージ座標）。 */
  readonly original: number;
  /**
   * ポインタを押した位置の、区間の法線方向の座標（ステージ座標）。ドラッグは押した位置からの移動量を
   * 区間へ足す（押した位置へ区間を跳ばさない）。キー操作では null。
   */
  readonly pointerOrigin: number | null;
  /** 有効な範囲へ寄せた後の、いまの位置。 */
  applied: number;
  /** いまの位置で作った手動経路。まだ動かしていなければ null。 */
  constraints: readonly ConnectorRouteConstraint[] | null;
  /** 上限で動かせないことを、この編集の中で既に通知したか。 */
  limitNotified: boolean;
  /** 保存の検証に通らない位置で動かせないことを、この編集の中で既に通知したか。 */
  invalidNotified: boolean;
}

/** 編集の開始に必要な値（開始する前に、動かせる軸を確かめるためにも使う）。 */
interface PreparedSession {
  readonly input: ConnectorGeometryInput;
  readonly geometry: ConnectorGeometry;
  readonly segmentIndex: number;
  readonly axis: ConnectorChannelAxis;
  readonly fromStraight: boolean;
  readonly snapshot: EditSnapshot;
  readonly original: number;
}

/** 区間を動かすと補うチャネルの数（端の区間は助走の先の折れ 2 本、直線の区間は 3 本）。 */
function addedChannelCount(geometry: ConnectorGeometry, segmentIndex: number): number {
  const role = geometry.segments[segmentIndex]?.role;
  if (role === "start" || role === "end") return 2;
  if (role === "single") return 3;
  return 0;
}

function frameOfObject(live: LiveFrame | undefined, object: CanvasObject | undefined) {
  if (live) return { x: live.x, y: live.y, w: live.w, h: live.h, rotation: live.rotation ?? 0 };
  if (object) {
    return { x: object.x, y: object.y, w: object.w, h: object.h, rotation: object.rotation ?? 0 };
  }
  return null;
}

function sameFrame(left: ConnectorFrame, right: ConnectorFrame): boolean {
  return (
    left.x === right.x &&
    left.y === right.y &&
    left.w === right.w &&
    left.h === right.h &&
    (left.rotation ?? 0) === (right.rotation ?? 0)
  );
}

/** 他の利用者の変更で、開始時点の想定が崩れたか（保存枠・色・太さの変化は編集を妨げない）。 */
function snapshotStillHolds(snapshot: EditSnapshot, objects: Record<string, CanvasObject>): boolean {
  const current = objects[snapshot.connector.id];
  if (!current || current.type !== "connector" || current.locked) return false;
  const before = resolveConnectorAppearance(snapshot.connector);
  const after = resolveConnectorAppearance(current);
  if (before.lineType !== after.lineType) return false;
  if (!isSameConnectorRoute(before.route, after.route)) return false;
  if (
    current.from.objectId !== snapshot.connector.from.objectId ||
    current.from.side !== snapshot.connector.from.side ||
    current.to.objectId !== snapshot.connector.to.objectId ||
    current.to.side !== snapshot.connector.to.side
  ) {
    return false;
  }
  const fromObj = objects[current.from.objectId];
  const toObj = objects[current.to.objectId];
  const fromFrame = frameOfObject(undefined, fromObj);
  const toFrame = frameOfObject(undefined, toObj);
  if (!fromFrame || !toFrame) return false;
  return sameFrame(fromFrame, snapshot.fromFrame) && sameFrame(toFrame, snapshot.toFrame);
}

/** 直線から始める編集で動かす区間（角丸の折れ線の自動経路の、動かせる区間の中央）。 */
function middleMovableSegment(geometry: ConnectorGeometry): number {
  const movable = geometry.segments
    .map((segment, index) => ({ segment, index }))
    .filter((entry) => entry.segment.axis !== null);
  return movable[Math.floor(movable.length / 2)]?.index ?? -1;
}

/**
 * 区間を動かせる範囲（区間の軸の座標）。チャネルの位置はキャンバス矩形の内側へ寄せ、保存の検証と同じ
 * 「始点アンカーからの相対値の上限」も超えないようにする（超えると保存の前の検証で止まる）。ただし元の
 * 位置は常に範囲に含める（キャンバスの外にある区間を、押した向きと逆へ跳ばさない）。
 */
function movableRangeFor(
  axis: ConnectorChannelAxis,
  anchor: ConnectorPoint,
  original: number
): { readonly min: number; readonly max: number } {
  const { min, max } = editableRangeFor(axis, anchor);
  return { min: Math.min(min, original), max: Math.max(max, original) };
}

/**
 * 区間を寄せる先の範囲（キャンバス矩形の内側で、始点アンカーからの相対値の上限も超えない範囲）。
 * {@link movableRangeFor} の元の位置を含める前の範囲。保存の検証が拒否するのは相対値の上限を超える位置
 * だけで、キャンバス矩形は編集の操作で寄せる範囲（キャンバスの外の位置も、相対値の上限の内側なら保存できる）。
 */
function editableRangeFor(
  axis: ConnectorChannelAxis,
  anchor: ConnectorPoint
): { readonly min: number; readonly max: number } {
  const sizePreset = useCanvasDocStore.getState().meta.sizePreset;
  const rect = resolveCanvasRect(normalizeCanvasSizePreset(sizePreset));
  const { min: limitMin, max: limitMax } = offsetLimitRangeFor(axis, anchor);
  const rectMin = axis === "x" ? rect.x : rect.y;
  const rectMax = axis === "x" ? rect.x + rect.w : rect.y + rect.h;
  return { min: Math.max(rectMin, limitMin), max: Math.min(rectMax, limitMax) };
}

/** 保存の検証と同じ「始点アンカーからの相対値の上限」の範囲（区間の軸の座標）。 */
function offsetLimitRangeFor(
  axis: ConnectorChannelAxis,
  anchor: ConnectorPoint
): { readonly min: number; readonly max: number } {
  const limits = connectorRouteOffsetLimitsFor(useCanvasDocStore.getState().meta.sizePreset);
  const base = axis === "x" ? anchor.x : anchor.y;
  const limit = axis === "x" ? limits.maxAbsX : limits.maxAbsY;
  return { min: base - limit, max: base + limit };
}

/**
 * 矢印キー 1 回で動かす先。ふつうはいまの位置（有効な範囲へ寄せた後）から step だけ動かす。元の位置が
 * 保存の検証と同じ相対値の上限の外にあり、まだ一度も動かせていない（保存の検証に通る下書きが無い）間は、
 * 上限の内側へ向かうキーで、寄せる先の範囲（{@link editableRangeFor}）の端へ寄せる（上限の外の位置から
 * 1 刻みずつ動かしても検証に通らず、キーではいつまでも戻せないため）。キャンバスの外にあるだけで上限の
 * 内側の位置は保存できるため、ふつうどおり 1 刻みずつ動かす。
 */
function keyboardTargetFor(session: RouteEditSession, step: number): number {
  const next = session.applied + step;
  if (session.constraints !== null) return next;
  const anchor = session.geometry.guide[0];
  if (!anchor) return next;
  const limitRange = offsetLimitRangeFor(session.axis, anchor);
  const editable = editableRangeFor(session.axis, anchor);
  if (step < 0 && session.applied > limitRange.max) return editable.max;
  if (step > 0 && session.applied < limitRange.min) return editable.min;
  return next;
}

export function CanvasConnectorRouteHandles(): JSX.Element | null {
  const dispatcher = useCanvasOpDispatcherContext();
  const showToast = useShowToast();
  const tool = useCanvasStore((s) => s.tool);
  const readOnly = useCanvasStore((s) => s.readOnly);
  const connectionDrafting = useCanvasStore((s) => s.connectionDraft !== null);
  const zoom = useCanvasStore((s) => s.view.zoom);
  const selectedId = useCanvasStore((s) =>
    s.selectedObjectIds.length === 1 ? s.selectedObjectIds[0] : undefined
  );
  const editingAvailable = useConnectorEditingAvailable();
  const connector = useCanvasDocStore((s) => {
    const object = selectedId ? s.objects[selectedId] : undefined;
    return object?.type === "connector" ? object : undefined;
  });
  const fromObj = useCanvasDocStore((s) =>
    connector ? s.objects[connector.from.objectId] : undefined
  );
  const toObj = useCanvasDocStore((s) => (connector ? s.objects[connector.to.objectId] : undefined));
  const fromLive = useConnectorDragStore((s) =>
    connector ? s.frames[connector.from.objectId] : undefined
  );
  const toLive = useConnectorDragStore((s) =>
    connector ? s.frames[connector.to.objectId] : undefined
  );

  // 進行中の編集。描画に使う値は state（再描画のため）、イベント処理は ref（最新値の参照）で持つ。
  const sessionRef = useRef<RouteEditSession | null>(null);
  const [activeHandle, setActiveHandle] = useState<{
    readonly point: ConnectorPoint;
    readonly axis: ConnectorChannelAxis;
  } | null>(null);
  const detachRef = useRef<(() => void) | null>(null);

  const endSession = useCallback((): void => {
    const session = sessionRef.current;
    sessionRef.current = null;
    detachRef.current?.();
    detachRef.current = null;
    setActiveHandle(null);
    if (session) useConnectorRouteDraftStore.getState().clearDraft(session.connectorId);
  }, []);

  const cancelSession = useCallback(
    (notify: boolean): void => {
      if (!sessionRef.current) return;
      endSession();
      if (notify) showToast("info", REMOTE_CHANGE_MESSAGE);
    },
    [endSession, showToast]
  );

  const commitSession = useCallback((): void => {
    const session = sessionRef.current;
    if (!session) return;
    const constraints = session.constraints;
    // 確定の直前にも、他の利用者の変更が入っていないかを確かめる（古い想定の経路を保存しない）。
    if (!snapshotStillHolds(session.snapshot, useCanvasDocStore.getState().objects)) {
      cancelSession(true);
      return;
    }
    // 送信の前に編集を終える（自分の楽観適用を「他の利用者の変更」と取り違えないため）。
    endSession();
    if (constraints !== null && session.applied !== session.original) {
      dispatcher.updateConnectors([session.connectorId], {
        // 動かした結果、折れの無い一直線に戻った（チャネルが残らない）場合は自動経路として保存する。
        route: constraints.length > 0 ? { mode: "manual", constraints } : { mode: "auto" },
        ...(session.fromStraight ? { lineType: "elbowed" as const } : {}),
      });
    }
  }, [cancelSession, dispatcher, endSession]);

  /** 目標の位置へ区間を動かし、下書きと表示中のハンドルを更新する。 */
  const moveTo = useCallback(
    (target: number): void => {
      const session = sessionRef.current;
      if (!session) return;
      const anchor = session.geometry.guide[0];
      if (!anchor) return;
      const range = movableRangeFor(session.axis, anchor, session.original);
      const clamped = Math.min(range.max, Math.max(range.min, target));
      const result = moveConnectorSegment({
        input: session.input,
        geometry: session.geometry,
        segmentIndex: session.segmentIndex,
        value: clamped,
        createId: generateCanvasOpId,
      });
      if (!result) {
        // 端の区間で補う折れがチャネルの上限を超える場合は、動かせない理由を 1 回だけ知らせる。
        const wouldExceed =
          session.geometry.channels.length +
            addedChannelCount(session.geometry, session.segmentIndex) >
          CONNECTOR_ROUTE_CONSTRAINTS_MAX;
        if (wouldExceed && !session.limitNotified) {
          session.limitNotified = true;
          showToast("info", ROUTE_LIMIT_MESSAGE);
        }
        return;
      }
      const route: ConnectorRoute =
        result.constraints.length > 0
          ? { mode: "manual", constraints: result.constraints }
          : { mode: "auto" };
      // 保存の検証（確定の op と同じ規則）に通らない経路は下書きにしない。キャンバスを縮めた後の復元などで
      // 上限を超えたチャネルを持つ経路は、上限の内側へ戻すまで動かせないことを 1 回だけ知らせる。
      const valid = validateConnectorPatchResult(
        session.snapshot.connector,
        { route, ...(session.fromStraight ? { lineType: "elbowed" as const } : {}) },
        connectorRouteOffsetLimitsFor(useCanvasDocStore.getState().meta.sizePreset)
      );
      if (!valid.ok) {
        if (!session.invalidNotified) {
          session.invalidNotified = true;
          showToast("info", valid.reason);
        }
        return;
      }
      session.applied = result.value;
      session.constraints = result.constraints;
      useConnectorRouteDraftStore.getState().setDraft({
        connectorId: session.connectorId,
        lineType: session.fromStraight ? "elbowed" : (session.input.lineType ?? "elbowed"),
        route,
      });
      const base = session.geometry.segments[session.segmentIndex]?.handlePoint;
      if (base) {
        setActiveHandle({
          axis: session.axis,
          point:
            session.axis === "x" ? { x: result.value, y: base.y } : { x: base.x, y: result.value },
        });
      }
    },
    [showToast]
  );

  /**
   * 編集の開始に必要な値を求める（まだ始めない）。動かせる区間が無ければ null。直線は角丸の折れ線の
   * 自動経路へ切り替えて、その中央の区間を動かす。
   */
  const prepareSession = useCallback(
    (segmentIndex: number): PreparedSession | null => {
      if (sessionRef.current || !connector) return null;
      const objects = useCanvasDocStore.getState().objects;
      const fromFrame = frameOfObject(undefined, objects[connector.from.objectId]);
      const toFrame = frameOfObject(undefined, objects[connector.to.objectId]);
      if (!fromFrame || !toFrame || connector.locked) return null;
      const appearance = resolveConnectorAppearance(connector);
      const fromStraight = appearance.lineType === "straight";
      const baseInput = connectorGeometryInputFor(connector, fromFrame, toFrame);
      const input: ConnectorGeometryInput = fromStraight
        ? { ...baseInput, lineType: "elbowed", route: { mode: "auto" } }
        : baseInput;
      const geometry = computeConnectorGeometry(input);
      const index = fromStraight ? middleMovableSegment(geometry) : segmentIndex;
      const segment = geometry.segments[index];
      if (!segment || segment.axis === null) return null;
      return {
        input,
        geometry,
        segmentIndex: index,
        axis: segment.axis,
        fromStraight,
        snapshot: { fromFrame, toFrame, connector },
        original: segment.axis === "x" ? segment.start.x : segment.start.y,
      };
    },
    [connector]
  );

  /** 求めた値で編集を始める。 */
  const beginSession = useCallback(
    (
      prepared: PreparedSession,
      originHandleIndex: number,
      kind: "pointer" | "keyboard",
      pointerId: number | null,
      pointerOrigin: number | null
    ): void => {
      sessionRef.current = {
        connectorId: prepared.snapshot.connector.id,
        kind,
        pointerId,
        input: prepared.input,
        geometry: prepared.geometry,
        segmentIndex: prepared.segmentIndex,
        originHandleIndex,
        axis: prepared.axis,
        fromStraight: prepared.fromStraight,
        snapshot: prepared.snapshot,
        original: prepared.original,
        pointerOrigin,
        applied: prepared.original,
        constraints: null,
        limitNotified: false,
        invalidNotified: false,
      };
      const segment = prepared.geometry.segments[prepared.segmentIndex];
      if (segment) setActiveHandle({ axis: prepared.axis, point: segment.handlePoint });
    },
    []
  );

  // 選択が外れた・編集できなくなった・アンマウントされたら、保存せずに取り消す。
  useEffect(() => {
    const session = sessionRef.current;
    if (!session) return;
    if (
      selectedId !== session.connectorId ||
      !editingAvailable ||
      readOnly ||
      tool !== "idle" ||
      connectionDrafting
    ) {
      cancelSession(false);
    }
  }, [cancelSession, connectionDrafting, editingAvailable, readOnly, selectedId, tool]);
  useEffect(() => () => endSession(), [endSession]);

  // 編集中に他の利用者が接続矢印・接続先の図形を変えた（削除・ロックを含む）ら、取り消して知らせる。
  useEffect(() => {
    return useCanvasDocStore.subscribe((state) => {
      const session = sessionRef.current;
      if (!session) return;
      if (!snapshotStillHolds(session.snapshot, state.objects)) cancelSession(true);
    });
  }, [cancelSession]);

  /**
   * ハンドルの pointerdown。キャンバス面（canvas-container）へ直接登録された範囲選択・パンの
   * pointerdown より先に止めるため、React の委譲（ルートで受ける）ではなく、ハンドルの包みへ直接登録した
   * リスナーから呼ぶ（既存のリサイズ・回転ハンドルと同じ形。選択操作の左右反転で左ボタンが範囲選択を
   * 担うときも、ハンドルのドラッグが範囲選択を始めない）。
   */
  const onPointerDown = (segmentIndex: number, element: HTMLElement, event: PointerEvent): void => {
    if (event.button !== 0 || event.isPrimary === false) return;
    // キャンバス面の範囲選択・パン・選択解除へ流さない。
    event.preventDefault();
    event.stopPropagation();
    const prepared = prepareSession(segmentIndex);
    if (!prepared) return;
    const down = screenToStage(
      { x: event.clientX, y: event.clientY },
      useCanvasStore.getState().view
    );
    beginSession(
      prepared,
      segmentIndex,
      "pointer",
      event.pointerId,
      prepared.axis === "x" ? down.x : down.y
    );
    try {
      element.setPointerCapture(event.pointerId);
    } catch {
      // 捕捉できない環境（合成イベント等）でも、window の購読で追従する。
    }
    let frame: number | null = null;
    let latest: { readonly x: number; readonly y: number } | null = null;
    const flush = (): void => {
      frame = null;
      if (!latest) return;
      const session = sessionRef.current;
      if (!session || session.pointerOrigin === null) return;
      const stage = screenToStage(latest, useCanvasStore.getState().view);
      // 押した位置からの移動量（区間の法線方向）だけを区間へ足す（押した位置へ区間を跳ばさない）。
      const moved = (session.axis === "x" ? stage.x : stage.y) - session.pointerOrigin;
      moveTo(session.original + moved);
    };
    const onMove = (ev: PointerEvent): void => {
      if (ev.pointerId !== sessionRef.current?.pointerId) return;
      latest = { x: ev.clientX, y: ev.clientY };
      // 位置の計算は描画の頻度に合わせる（pointermove ごとに経路探索をしない）。
      if (frame === null) frame = window.requestAnimationFrame(flush);
    };
    const onUp = (ev: PointerEvent): void => {
      if (ev.pointerId !== sessionRef.current?.pointerId) return;
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
        flush();
      }
      commitSession();
    };
    const onCancel = (ev: PointerEvent): void => {
      if (ev.pointerId !== sessionRef.current?.pointerId) return;
      cancelSession(false);
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key !== "Escape") return;
      // 選択の解除（全体のショートカット）へ流さず、編集だけを取り消す。
      ev.preventDefault();
      ev.stopPropagation();
      cancelSession(false);
    };
    const onBlur = (): void => cancelSession(false);
    window.addEventListener("pointermove", onMove, true);
    window.addEventListener("pointerup", onUp, true);
    window.addEventListener("pointercancel", onCancel, true);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("blur", onBlur);
    detachRef.current = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", onUp, true);
      window.removeEventListener("pointercancel", onCancel, true);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("blur", onBlur);
      try {
        if (element.hasPointerCapture(event.pointerId)) {
          element.releasePointerCapture(event.pointerId);
        }
      } catch {
        // 要素が既に外れている場合は何もしない。
      }
    };
  };

  const onKeyDown = (segmentIndex: number, event: React.KeyboardEvent<HTMLDivElement>): void => {
    const session = sessionRef.current;
    if (event.key === "Escape") {
      if (!session) return;
      event.preventDefault();
      event.stopPropagation();
      cancelSession(false);
      return;
    }
    if (event.key === "Enter") {
      if (!session) return;
      event.preventDefault();
      event.stopPropagation();
      commitSession();
      return;
    }
    const direction =
      event.key === "ArrowLeft" || event.key === "ArrowUp"
        ? -1
        : event.key === "ArrowRight" || event.key === "ArrowDown"
          ? 1
          : 0;
    if (direction === 0) return;
    // ページのスクロール・他のショートカットへ流さない。
    event.preventDefault();
    event.stopPropagation();
    const horizontalKey = event.key === "ArrowLeft" || event.key === "ArrowRight";
    if (!session) {
      // 縦の区間は左右、横の区間は上下だけに動く。動かせない向きのキーでは編集を始めない
      // （始めると他のハンドルが隠れたまま何も動かない）。
      const prepared = prepareSession(segmentIndex);
      if (!prepared || (prepared.axis === "x") !== horizontalKey) return;
      beginSession(prepared, segmentIndex, "keyboard", null, null);
    }
    const active = sessionRef.current;
    if (!active) return;
    if ((active.axis === "x") !== horizontalKey) return;
    const stepCssPx = event.shiftKey ? 10 : 1;
    const view = useCanvasStore.getState().view;
    // いまの位置（有効な範囲へ寄せた後）から動かす。止まった位置を越えて目標だけが進まないようにする。
    // （元の位置が保存の検証の相対値の上限の外のときは、上限の内側へ向かうキーで寄せる先の範囲の端へ寄せる。
    // keyboardTargetFor を参照。）
    moveTo(keyboardTargetFor(active, (direction * stepCssPx) / view.zoom));
  };

  const onBlurHandle = (): void => {
    // キー操作の途中でフォーカスが外れたら、確定せずに取り消す。
    if (sessionRef.current?.kind === "keyboard") cancelSession(false);
  };

  // ハンドルの pointerdown は、ハンドルの包みへ直接登録したリスナーで受ける（onPointerDown の説明を参照）。
  // リスナーは包みの要素ごとに 1 つだけ登録し、処理は描画ごとの最新の onPointerDown へ委ねる。
  const pointerDownRef = useRef(onPointerDown);
  pointerDownRef.current = onPointerDown;
  const wrapperListenerRef = useRef<{
    readonly element: HTMLDivElement;
    readonly listener: (event: PointerEvent) => void;
  } | null>(null);
  const setWrapperElement = useCallback((element: HTMLDivElement | null): void => {
    const previous = wrapperListenerRef.current;
    if (previous && previous.element === element) return;
    if (previous) previous.element.removeEventListener("pointerdown", previous.listener);
    wrapperListenerRef.current = null;
    if (!element) return;
    const listener = (event: PointerEvent): void => {
      const handleElement =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>("[data-route-handle-index]")
          : null;
      if (!handleElement || !element.contains(handleElement)) return;
      const index = Number(handleElement.dataset.routeHandleIndex);
      if (!Number.isInteger(index)) return;
      pointerDownRef.current(index, handleElement, event);
    };
    element.addEventListener("pointerdown", listener);
    wrapperListenerRef.current = { element, listener };
  }, []);

  if (
    readOnly ||
    tool !== "idle" ||
    connectionDrafting ||
    !editingAvailable ||
    !connector ||
    connector.locked
  ) {
    return null;
  }
  const fromFrame = frameOfObject(fromLive, fromObj);
  const toFrame = frameOfObject(toLive, toObj);
  if (!fromFrame || !toFrame) return null;
  const k = 1 / zoom;
  // 編集中は下書きの位置のハンドル 1 つだけを描くため、確定済みの経路は求めない。
  const geometry = activeHandle
    ? null
    : computeConnectorGeometry(connectorGeometryInputFor(connector, fromFrame, toFrame));
  const straight = geometry?.lineType === "straight";
  // 編集中は、編集を始めたハンドルの要素だけを（同じ key のまま）動かして描く。
  const handles = activeHandle
    ? [
        {
          index: sessionRef.current?.originHandleIndex ?? 0,
          point: activeHandle.point,
          axis: activeHandle.axis,
          active: true,
        },
      ]
    : (geometry?.segments ?? [])
        .map((segment, index) => ({
          index,
          point: segment.handlePoint,
          axis: segment.axis,
          active: false,
        }))
        .filter((handle) => handle.axis !== null || straight);

  return (
    <div
      ref={setWrapperElement}
      data-testid="connector-route-handles"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: Number.MAX_SAFE_INTEGER }}
    >
      {handles.map((handle, order) => {
        const label =
          straight && !handle.active
            ? "接続矢印の経路（ドラッグすると角丸の折れ線に切り替えて経路を変えます）"
            : handle.axis === "x"
              ? `接続矢印の経路の区間 ${handle.index + 1}（左右へ移動。矢印キーで調整し Enter で確定、Esc で取り消し）`
              : `接続矢印の経路の区間 ${handle.index + 1}（上下へ移動。矢印キーで調整し Enter で確定、Esc で取り消し）`;
        return (
          <div
            key={`segment-${handle.index}`}
            data-testid={
              handle.active ? "connector-route-handle-active" : `connector-route-handle-${order}`
            }
            data-axis={handle.axis ?? "straight"}
            data-route-handle-index={handle.index}
            role="button"
            tabIndex={0}
            aria-label={label}
            title={label}
            onKeyDown={(event) => onKeyDown(handle.index, event)}
            onBlur={onBlurHandle}
            style={{
              position: "absolute",
              left: `${handle.point.x}px`,
              top: `${handle.point.y}px`,
              width: `${HANDLE_HIT_PX}px`,
              height: `${HANDLE_HIT_PX}px`,
              marginLeft: `${-HANDLE_HIT_PX / 2}px`,
              marginTop: `${-HANDLE_HIT_PX / 2}px`,
              // 当たり判定と表示を 1/zoom 補正で画面上一定の大きさにする。
              transform: `scale(${k})`,
              transformOrigin: "center",
              display: "grid",
              placeItems: "center",
              pointerEvents: "auto",
              cursor: handle.axis === "x" ? "ew-resize" : handle.axis === "y" ? "ns-resize" : "move",
              touchAction: "none",
              outlineOffset: "2px",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: `${HANDLE_VISUAL_PX}px`,
                height: `${HANDLE_VISUAL_PX}px`,
                borderRadius: "50%",
                background: "var(--color-surface)",
                border: "2px solid var(--color-selection)",
                boxSizing: "border-box",
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
